import { useEffect, useId, useMemo, useState } from 'react'

import Button from '@/components/ui/Button'
import type { MenuOption } from '@/components/ui/MenuItem'
import MenuSingle from '@/components/ui/MenuSingle'
import Modal from '@/components/ui/Modal'
import SearchInput from '@/components/ui/SearchInput'
import SelectTrigger from '@/components/ui/SelectTrigger'
import TermTip from '@/components/ui/TermTip'
import { fieldVariants } from '@/components/ui/field-variants'
import { useScreenerCount } from '@/features/screener/api/queries'
import type { ColumnSet } from '@/features/screener/lib/column-sets'
import { fromInputValue, toInputValue, toRange, unitAffixes } from '@/features/screener/lib/filters'
import { formatQuantity } from '@/lib/format'
import type { Money } from '@/lib/money'
import { cn } from '@/lib/utils'
import { moneySchema } from '@/schemas/common'
import type {
  Metric,
  MetricRange,
  MetricRanges,
  ScreenerSector,
  ScreenFilter,
} from '@/schemas/screener'

/** How long the draft must sit still before its count is asked for. */
const COUNT_DEBOUNCE_MS = 300

const ALL_SECTORS = 'all'

/** One metric's two fields, as typed: text until it reads as a number. */
interface DraftBounds {
  readonly min: string
  readonly max: string
}

type Draft = Readonly<Record<string, DraftBounds>>

type RowCheck =
  | { readonly kind: 'ok'; readonly range: MetricRange | null }
  | { readonly kind: 'error'; readonly message: string }

const readBound = (metric: Metric, text: string): Money | null | 'invalid' => {
  if (text.trim() === '') return null
  const parsed = moneySchema.safeParse(text.trim().replaceAll(',', ''))
  return parsed.success ? fromInputValue(metric, parsed.data) : 'invalid'
}

/**
 * A row's fields as a range, or why not. A minimum over its maximum is refused here: mm-be answers
 * one with a 400, so it never reaches the count or the screen.
 */
const checkRow = (metric: Metric, bounds: DraftBounds | undefined): RowCheck => {
  if (bounds === undefined) return { kind: 'ok', range: null }
  const min = readBound(metric, bounds.min)
  const max = readBound(metric, bounds.max)
  if (min === 'invalid' || max === 'invalid') return { kind: 'error', message: 'Enter a number.' }
  const range = toRange(min, max)
  if (range === null && min !== null && max !== null) {
    return { kind: 'error', message: 'The minimum is above the maximum.' }
  }
  return { kind: 'ok', range }
}

const draftOf = (metrics: readonly Metric[], ranges: MetricRanges): Draft =>
  Object.fromEntries(
    metrics.flatMap((metric) => {
      const range = ranges[metric.key]
      if (range === undefined) return []
      return [
        [
          metric.key,
          {
            min: range.min === null ? '' : toInputValue(metric, range.min),
            max: range.max === null ? '' : toInputValue(metric, range.max),
          },
        ],
      ]
    }),
  )

/** The value after it has held still for `delay`: the count asks once the typing stops. */
function useSettled<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => {
      setSettled(value)
    }, delay)
    return () => {
      clearTimeout(id)
    }
  }, [value, delay])
  return settled
}

interface FiltersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sets: readonly ColumnSet[]
  /** The metrics in `/metrics/` order: the groups and their rows. */
  metrics: readonly Metric[]
  sectors: readonly ScreenerSector[]
  applied: ScreenFilter
  /** How many stocks the unfiltered screen has: the footer's "of M". */
  total: number | undefined
  onApply: (filter: ScreenFilter) => void
}

/**
 * The mock's Filters dialog (`oxe`): a search over the filters, the sector, then every metric in
 * its group, and a footer that counts the matches as the draft changes. The mock's preset chips
 * cannot come from mm-be, so each row is the metric's name (its term card is mm-be's own
 * description) and a Min and a Max field in the metric's unit. Nothing applies until "Show N
 * matches"; closing drops the draft.
 *
 * Mounted afresh for each opening (the caller keys it), so the draft always starts from what is
 * applied.
 */
export default function FiltersDialog({
  open,
  onOpenChange,
  sets,
  metrics,
  sectors,
  applied,
  total,
  onApply,
}: FiltersDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(metrics, applied.ranges))
  const [sector, setSector] = useState<string | null>(applied.sector)
  const [find, setFind] = useState('')
  const [sectorOpen, setSectorOpen] = useState(false)

  const checks = useMemo(
    () => new Map(metrics.map((metric) => [metric.key, checkRow(metric, draft[metric.key])])),
    [metrics, draft],
  )
  const invalid = [...checks.values()].some((check) => check.kind === 'error')
  const filter = useMemo<ScreenFilter>(() => {
    const ranges: Record<string, MetricRange> = {}
    for (const [key, check] of checks) {
      if (check.kind === 'ok' && check.range !== null) ranges[key] = check.range
    }
    return { ranges, sector }
  }, [checks, sector])
  const settled = useSettled(filter, COUNT_DEBOUNCE_MS)
  const count = useScreenerCount(settled, { enabled: !invalid })
  const matches = invalid ? undefined : count.data
  const onCount = Object.keys(filter.ranges).length + (sector === null ? 0 : 1)
  const canApply = !invalid && matches !== 0

  const needle = find.trim().toLowerCase()
  const groups = sets
    .map((set) => ({
      set,
      rows: metrics.filter(
        (metric) =>
          metric.group === set.id &&
          (needle === '' ||
            `${metric.label} ${metric.description} ${set.label}`.toLowerCase().includes(needle)),
      ),
    }))
    .filter((group) => group.rows.length > 0)

  const sectorOptions: readonly MenuOption[] = [
    { value: ALL_SECTORS, label: 'All sectors' },
    ...sectors.map((option) => ({ value: option.key, label: option.name })),
  ]
  const sectorLabel =
    sector === null
      ? 'All sectors'
      : (sectors.find((option) => option.key.toLowerCase() === sector.toLowerCase())?.name ??
        sector)

  const setBound = (key: string, side: keyof DraftBounds, text: string) => {
    setDraft((previous) => {
      const bounds = { ...(previous[key] ?? { min: '', max: '' }), [side]: text }
      const rest = Object.entries(previous).filter(([other]) => other !== key)
      return Object.fromEntries(
        bounds.min === '' && bounds.max === '' ? rest : [...rest, [key, bounds]],
      )
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Filters"
      size="640"
      footerNote={
        <p aria-live="polite" className="text-body-m">
          <span className="text-h-section tnum text-foreground">
            {matches === undefined ? '—' : formatQuantity(matches)}
          </span>{' '}
          <span className="text-muted-foreground-subtle">
            {total === undefined ? 'match' : `of ${formatQuantity(total)} match`}
          </span>
        </p>
      }
      footer={
        <>
          <Button
            variant="secondary"
            disabled={onCount === 0}
            onClick={() => {
              setDraft({})
              setSector(null)
            }}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            className="lg:min-w-38"
            disabled={!canApply}
            onClick={() => {
              onApply(filter)
            }}
          >
            {matches === 0
              ? 'No matches'
              : `Show ${matches === undefined ? '' : `${formatQuantity(matches)} `}${matches === 1 ? 'match' : 'matches'}`}
          </Button>
        </>
      }
    >
      <div className="flex min-h-0 flex-auto flex-col">
        <div className="grid shrink-0 gap-3 border-b border-border pb-3 sm:grid-cols-[minmax(0,1fr)_--spacing(48)]">
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-body-xs text-muted-foreground-subtle">Search filters</span>
            <SearchInput
              aria-label="Search filters"
              placeholder="Type a word such as P/E, dividend or volume"
              value={find}
              onChange={(event) => {
                setFind(event.target.value)
              }}
            />
          </label>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-body-xs text-muted-foreground-subtle">Sector</span>
            <MenuSingle
              options={sectorOptions}
              value={sector ?? ALL_SECTORS}
              onChange={(next) => {
                setSector(next === ALL_SECTORS ? null : next)
              }}
              open={sectorOpen}
              onOpenChange={setSectorOpen}
              label="Sector"
              searchPlaceholder="Search sectors"
              trigger={(triggerProps) => (
                <SelectTrigger
                  {...triggerProps}
                  aria-label={`Sector, ${sectorLabel}`}
                  label={sectorLabel}
                  open={sectorOpen}
                  className={cn(
                    'w-full justify-between',
                    sector !== null && 'border-border-accent',
                  )}
                />
              )}
            />
          </div>
        </div>

        <div className="-mx-1 min-h-0 flex-auto overflow-y-auto overscroll-contain scrollbar-slim px-1 pt-1 pb-3">
          {groups.length === 0 ? (
            <p className="py-6 text-body-m text-muted-foreground">No filter matches “{find}”</p>
          ) : (
            groups.map(({ set, rows }) => {
              const on = rows.filter((metric) => filter.ranges[metric.key] !== undefined).length
              return (
                <section key={set.id} className="mt-1 first:mt-0">
                  <h3 className="flex items-center gap-2 pt-3 pb-1 text-section-label text-muted-foreground-subtle uppercase">
                    {set.label}
                    {on > 0 ? (
                      <span className="rounded-full bg-tone-accent-soft px-2 text-body-xs font-semibold text-accent-soft-foreground normal-case">
                        {on} on
                      </span>
                    ) : null}
                  </h3>
                  {rows.map((metric) => (
                    <FilterRow
                      key={metric.key}
                      metric={metric}
                      bounds={draft[metric.key]}
                      check={checks.get(metric.key)}
                      onChange={(side, text) => {
                        setBound(metric.key, side, text)
                      }}
                    />
                  ))}
                </section>
              )
            })
          )}
        </div>
      </div>
    </Modal>
  )
}

function FilterRow({
  metric,
  bounds,
  check,
  onChange,
}: {
  metric: Metric
  bounds: DraftBounds | undefined
  check: RowCheck | undefined
  onChange: (side: keyof DraftBounds, text: string) => void
}) {
  const errorId = useId()
  const error = check?.kind === 'error' ? check.message : null
  const set = check?.kind === 'ok' && check.range !== null
  return (
    <div className="grid min-h-11 items-center gap-x-3 gap-y-1 border-t border-border-subtle py-2 first-of-type:border-t-0 sm:grid-cols-[--spacing(38)_minmax(0,1fr)]">
      <p
        className={cn(
          'text-body-m transition-colors',
          set ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        <TermTip title={metric.label} body={metric.description}>
          {metric.label}
        </TermTip>
      </p>
      <div className="flex min-w-0 items-center gap-2">
        <BoundField
          metric={metric}
          side="min"
          value={bounds?.min ?? ''}
          invalid={error !== null}
          errorId={error === null ? undefined : errorId}
          onChange={onChange}
        />
        <span aria-hidden="true" className="text-muted-foreground-subtle">
          –
        </span>
        <BoundField
          metric={metric}
          side="max"
          value={bounds?.max ?? ''}
          invalid={error !== null}
          errorId={error === null ? undefined : errorId}
          onChange={onChange}
        />
      </div>
      {error === null ? null : (
        <p id={errorId} role="alert" className="text-body-xs text-destructive sm:col-start-2">
          {error}
        </p>
      )}
    </div>
  )
}

function BoundField({
  metric,
  side,
  value,
  invalid,
  errorId,
  onChange,
}: {
  metric: Metric
  side: keyof DraftBounds
  value: string
  invalid: boolean
  errorId: string | undefined
  onChange: (side: keyof DraftBounds, text: string) => void
}) {
  const { prefix, suffix } = unitAffixes(metric)
  const word = side === 'min' ? 'Min' : 'Max'
  return (
    <label className={cn(fieldVariants({ invalid }), 'h-11 min-w-0 flex-1 gap-1 px-3 lg:h-9')}>
      {prefix === undefined ? null : (
        <span aria-hidden="true" className="text-body-xs text-muted-foreground-subtle">
          {prefix}
        </span>
      )}
      <input
        inputMode="decimal"
        autoComplete="off"
        aria-label={`${metric.label}, ${word.toLowerCase()}imum`}
        aria-invalid={invalid || undefined}
        aria-describedby={errorId}
        placeholder={word}
        value={value}
        onChange={(event) => {
          onChange(side, event.target.value)
        }}
        className="min-w-0 flex-1 bg-transparent text-body-l tnum text-foreground outline-none placeholder:text-muted-foreground-subtle lg:text-body-m"
      />
      {suffix === undefined ? null : (
        <span aria-hidden="true" className="text-body-xs text-muted-foreground-subtle">
          {suffix}
        </span>
      )}
    </label>
  )
}
